import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Switch, Typography, useTheme } from '@mui/material';
import React from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import type { FormValues } from '../../organisms/AddRuleDrawer/useAddRuleDrawer';
import { conditionOptions } from '../../utils/constants';
import { RuleDrawerVariant } from '../../utils/types';
import { Dropdown, DropdownOption } from '../Dropdown';
import { SectionHeader } from '../SectionHeader/SectionHeader';

interface Props {
  control: Control<FormValues>;
  conditionValueOptions: DropdownOption[];
  isLoadingConditionValues: boolean;
  variant?: RuleDrawerVariant;
}

export const ConditionBuilderSection: React.FC<Props> = ({
  control,
  conditionValueOptions,
  isLoadingConditionValues,
  variant,
}) => {
  const theme = useTheme<LensTheme>();

  const sp = theme.tokens.semantic.spacing.desktop.gap;
  const color = theme.tokens.semantic.color;

  const selectedCondition = useWatch({ control, name: 'selectedCondition' });
  const applyToAllCases = useWatch({ control, name: 'applyToAllCases' });

  const shouldShowConditionDropdowns = !applyToAllCases;
  const shouldShowConditionValue =
    shouldShowConditionDropdowns && selectedCondition;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sp[3].value,
        paddingTop: sp[4].value,
        ...(variant === RuleDrawerVariant.NEW && {
          borderTop: `1px solid ${color.ui.divider.default.value}`,
        }),
      }}
    >
      <SectionHeader
        title="Condition Builder"
        subtitle="Define conditions for when this retention policy should apply"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: sp[1].value }}>
        <Controller
          name="applyToAllCases"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              slotProps={{
                input: {
                  sx: {
                    width: '100% !important',
                    height: '100% !important',
                  },
                },
              }}
            />
          )}
        />

        <Typography
          component="span"
          sx={{
            fontSize: '16px',
            fontWeight: 400,
            color: color.type.default.value,
          }}
        >
          Apply rule for all cases
        </Typography>
      </Box>

      {shouldShowConditionDropdowns && (
        <Controller
          name="selectedCondition"
          control={control}
          render={({ field }) => (
            <Dropdown
              placeholder="Select condition"
              value={field.value}
              onChange={field.onChange}
              options={conditionOptions}
            />
          )}
        />
      )}

      {shouldShowConditionValue && (
        <Controller
          name="selectedConditionValue"
          control={control}
          render={({ field }) => (
            <Dropdown
              placeholder="Select condition value"
              value={field.value}
              onChange={field.onChange}
              options={conditionValueOptions}
              disabled={isLoadingConditionValues}
            />
          )}
        />
      )}
    </Box>
  );
};
